/**
 * @fileoverview
 * Camera Capture Modal Component - Forces live camera capture instead of file upload
 * Uses MediaDevices API for direct camera access
 * 
 * @version 1.0.0
 * @author KATALYST TEAM
 */

import { Component, EventEmitter, Output, ViewChild, ElementRef, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActionButtonComponent } from '../action-button/action-button.component';

@Component({
  selector: 'app-camera-capture-modal',
  standalone: true,
  imports: [CommonModule, ActionButtonComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './camera-capture-modal.component.html',
  styleUrl: './camera-capture-modal.component.css',
})
export class CameraCaptureModalComponent implements OnDestroy {
  @ViewChild('videoElement') videoElement!: ElementRef<HTMLVideoElement>;
  @ViewChild('canvasElement') canvasElement!: ElementRef<HTMLCanvasElement>;
  
  @Output() imageCaptured = new EventEmitter<{ file: File; dataUrl: string }>();
  @Output() cancelled = new EventEmitter<void>();

  isVisible = signal(false);
  cameraStream = signal<MediaStream | null>(null);
  capturedImage = signal<string | null>(null);
  loading = signal(false);
  error = signal<string | null>(null);

  constructor(private cdr: ChangeDetectorRef) {}

  /**
   * Open camera modal and start camera stream
   */
  async open() {
    this.isVisible.set(true);
    this.loading.set(true);
    this.error.set(null);
    this.capturedImage.set(null);

    try {
      // Request camera access with rear camera preference
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment', // Prefer rear camera on mobile
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        },
        audio: false
      });

      this.cameraStream.set(stream);
      this.loading.set(false);

      // Wait for video element to be available and attach stream
      setTimeout(() => {
        this.attachStreamToVideo(stream);
        this.cdr.detectChanges();
      }, 100);
    } catch (err: any) {
      console.error('Camera access error:', err);
      let errorMessage = 'Failed to access camera';
      
      if (err.name === 'NotAllowedError') {
        errorMessage = 'Camera access denied. Please allow camera permission.';
      } else if (err.name === 'NotFoundError') {
        errorMessage = 'No camera found on this device.';
      } else if (err.name === 'NotReadableError') {
        errorMessage = 'Camera is already in use by another application.';
      }
      
      this.error.set(errorMessage);
      this.loading.set(false);
      this.cdr.detectChanges();
    }
  }

  /**
   * Attach media stream to video element
   */
  private attachStreamToVideo(stream: MediaStream) {
    if (this.videoElement && this.videoElement.nativeElement) {
      const video = this.videoElement.nativeElement;
      video.srcObject = stream;
      
      // Ensure video starts playing
      video.play().catch(err => {
        console.error('Error playing video:', err);
      });
    }
  }

  /**
   * Capture photo from video stream
   */
  capturePhoto() {
    if (!this.videoElement || !this.canvasElement) {
      return;
    }

    const video = this.videoElement.nativeElement;
    const canvas = this.canvasElement.nativeElement;
    const context = canvas.getContext('2d');

    if (!context) {
      this.error.set('Failed to initialize canvas');
      return;
    }

    // Verify video is playing and has valid dimensions
    if (video.readyState !== video.HAVE_ENOUGH_DATA) {
      this.error.set('Camera feed not ready. Please wait a moment and try again.');
      return;
    }

    if (video.videoWidth === 0 || video.videoHeight === 0) {
      this.error.set('Invalid camera dimensions. Please restart the camera.');
      return;
    }

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw current video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Get image data URL
    const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
    this.capturedImage.set(dataUrl);
    
    // Note: We keep the video stream active so retake can work smoothly
    this.cdr.detectChanges();
  }

  /**
   * Retake photo - clears captured image and returns to live preview
   */
  retakePhoto() {
    this.capturedImage.set(null);
    
    // Ensure video stream is attached and playing
    setTimeout(() => {
      const stream = this.cameraStream();
      if (stream && this.videoElement && this.videoElement.nativeElement) {
        const video = this.videoElement.nativeElement;
        
        // Re-attach stream if needed
        if (!video.srcObject) {
          video.srcObject = stream;
        }
        
        // Ensure video is playing
        if (video.paused) {
          video.play().catch(err => {
            console.error('Error resuming video:', err);
          });
        }
      }
      this.cdr.detectChanges();
    }, 50);
  }

  /**
   * Confirm and save captured image
   */
  async savePhoto() {
    const imageDataUrl = this.capturedImage();
    if (!imageDataUrl) {
      return;
    }

    // Convert data URL to File object
    const file = await this.dataURLtoFile(imageDataUrl, `capture_${Date.now()}.jpg`);
    
    // Emit the captured image
    this.imageCaptured.emit({
      file,
      dataUrl: imageDataUrl
    });

    this.close();
  }

  /**
   * Cancel capture and close modal
   */
  cancel() {
    this.cancelled.emit();
    this.close();
  }

  /**
   * Close modal and stop camera stream
   */
  close() {
    this.stopCamera();
    this.isVisible.set(false);
    this.capturedImage.set(null);
    this.error.set(null);
    this.cdr.detectChanges();
  }

  /**
   * Stop camera stream
   */
  private stopCamera() {
    const stream = this.cameraStream();
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      this.cameraStream.set(null);
    }
  }

  /**
   * Convert data URL to File object
   */
  private async dataURLtoFile(dataUrl: string, filename: string): Promise<File> {
    const response = await fetch(dataUrl);
    const blob = await response.blob();
    return new File([blob], filename, { type: 'image/jpeg' });
  }

  /**
   * Cleanup on component destroy
   */
  ngOnDestroy() {
    this.stopCamera();
  }
}

